import { Box, Button, Container, Typography, useTheme, Grid, Card, CardContent, Chip } from '@mui/material';
import { Link } from 'react-router-dom';
import { useMemo } from 'react';
import { TrendingUp, Security, People, Phone } from '@mui/icons-material';

const Hero = () => {
  const theme = useTheme();
  
  // Memoized styles for performance
  const heroStyles = useMemo(() => ({
    bgcolor: 'primary.main',
    color: 'white',
    pt: { xs: '100px', md: '120px' },
    pb: { xs: 8, md: 10 },
    position: 'relative',
    background: theme.palette.mode === 'dark'
      ? 'linear-gradient(135deg, #424242 0%, #212121 100%)'
      : 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)',
    width: '100%',
    overflow: 'hidden',
    '&:after': {
      content: '""',
      position: 'absolute',
      bottom: '-2px',
      left: 0,
      right: 0,
      height: { xs: '60px', md: '80px' },
      background: theme.palette.mode === 'dark'
        ? 'linear-gradient(to bottom, rgba(18,18,18,0) 0%, rgba(18,18,18,1) 100%)'
        : 'linear-gradient(to bottom, rgba(245,245,245,0) 0%, rgba(245,245,245,1) 100%)',
      zIndex: 1,
      pointerEvents: 'none'
    }
  }), [theme.palette.mode]);

  const valuePropositionCards = [
    {
      icon: <Security sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Get Best Insurance Plans',
      description: 'Compare and buy Tata AIA policies with expert guidance',
      cta: 'Explore Plans',
      link: '/services',
      color: '#1976d2'
    },
    {
      icon: <TrendingUp sx={{ fontSize: 40, color: 'success.main' }} />,
      title: 'Become an Agent',
      description: 'Earn side income by selling insurance policies',
      cta: 'Join as Agent',
      link: '/join',
      color: '#2e7d32'
    }
  ];

  return (
    <Box
      component="section"
      aria-label="Hero section"
      sx={heroStyles}
    >
      <Container maxWidth="lg">
        {/* Main Headline */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Chip 
            label="🏆 Tata AIA Certified Agent" 
            sx={{ 
              mb: 3, 
              bgcolor: 'rgba(255,255,255,0.2)', 
              color: 'white',
              fontWeight: 600,
              fontSize: '0.9rem',
              px: 2,
              py: 0.5
            }} 
          />
          
          <Typography
            variant="h1"
            component="h1"
            sx={{
              color: 'white',
              textShadow: '0 2px 4px rgba(0,0,0,0.1)',
              fontSize: { xs: '2rem', sm: '2.75rem', md: '3.5rem' },
              fontWeight: 800,
              lineHeight: { xs: 1.1, md: 1.05 },
              mb: 3,
              letterSpacing: '-0.02em'
            }}
          >
            Your Gateway to
            <Box component="span" sx={{ display: 'block', color: '#FFD700' }}>
              Financial Security
            </Box>
          </Typography>
          
          <Typography
            variant="h2"
            component="p"
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              maxWidth: '700px',
              mx: 'auto',
              fontWeight: 400,
              textShadow: '0 1px 2px rgba(0,0,0,0.1)',
              fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.375rem' },
              lineHeight: 1.5,
              mb: 2
            }}
          >
            Get expert advice on Tata AIA insurance plans or start earning by becoming an agent
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: { xs: '1rem', md: '1.125rem' },
              mb: 6
            }}
          >
            Serving Secunderabad & Telangana • 500+ Happy Customers • 5★ Rating
          </Typography>
        </Box>

        {/* Dual Value Proposition Cards */}
        <Grid container spacing={3} sx={{ mb: 6 }}>
          {valuePropositionCards.map((card, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 4,
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  border: `2px solid ${card.color}20`,
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 20px 40px rgba(0,0,0,0.1)`,
                    border: `2px solid ${card.color}40`
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {card.icon}
                    <Typography 
                      variant="h3" 
                      sx={{ 
                        ml: 2, 
                        fontWeight: 700, 
                        color: 'text.primary',
                        fontSize: '1.5rem'
                      }}
                    >
                      {card.title}
                    </Typography>
                  </Box>
                  
                  <Typography 
                    variant="body1" 
                    color="text.secondary" 
                    sx={{ mb: 3, lineHeight: 1.6 }}
                  >
                    {card.description}
                  </Typography>
                  
                  <Button
                    component={Link}
                    to={card.link}
                    variant="contained"
                    fullWidth
                    sx={{
                      bgcolor: card.color,
                      py: 1.5,
                      fontWeight: 600,
                      fontSize: '1rem',
                      borderRadius: 2,
                      '&:hover': {
                        bgcolor: card.color,
                        filter: 'brightness(1.1)',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    {card.cta}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Quick Contact CTA */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: 'white' }}>
            Need Immediate Assistance?
          </Typography>
          
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            justifyContent: 'center',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            maxWidth: 500,
            mx: 'auto'
          }}>
            <Button
              href="https://wa.me/916281648568?text=Hi%20PolicyTap,%20I'm%20interested%20in%20Tata%20AIA%20insurance%20plans."
              target="_blank"
              variant="contained"
              startIcon={<Phone />}
              sx={{
                bgcolor: '#25D366',
                color: 'white',
                px: 4,
                py: 1.5,
                fontWeight: 600,
                borderRadius: 3,
                minWidth: { xs: '100%', sm: 200 },
                '&:hover': {
                  bgcolor: '#128C7E',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              WhatsApp Now
            </Button>
            
            <Button
              href="tel:+916281648568"
              variant="outlined"
              startIcon={<Phone />}
              sx={{
                borderColor: 'white',
                color: 'white',
                px: 4,
                py: 1.5,
                fontWeight: 600,
                borderRadius: 3,
                minWidth: { xs: '100%', sm: 200 },
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.1)',
                  borderColor: 'white',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              Call Direct
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Hero;
